import { EngineStore, Scene } from '@babylonjs/core';
import { BabylonjsModule, BabylonjsModuleOptions } from '@modules/babylonjs/src/BabylonjsModule';
import { Resolution } from '@modules/babylonjs/src/QualitySettings';
import { Injectable } from '@modules/core/src/injectable/Injectable';

import { loadJson } from '../loader/JsonLoader';
import { PerformanceProfiler } from '../manager';
import { RenderManager } from '../manager/RenderManager';
import { onAllModelLoaded, onUpdatePostprocessOptionsFromJson } from '../observable/CommonObservable';
import { Profile } from '../util/Profile';

import { PostprocessOptions, PostprocessParameters, ZEDRenderingPipeline } from './postprocess';

export class GraphicAPI implements Injectable {
  static readonly INJECTABLE_TOKEN = Symbol.for('GraphicAPI');

  //-------------------------------------------------
  // * Control Postprocess
  protected postProcess?: ZEDRenderingPipeline;

  private scene: Scene;
  private options: BabylonjsModuleOptions;
  private readonly renderManager;
  private readonly performanceProfiler;
  constructor({
    [BabylonjsModule.INJECTABLE_TOKEN]: babylonjs,
    [RenderManager.INJECTABLE_TOKEN]: renderManager,
    [PerformanceProfiler.INJECTABLE_TOKEN]: performanceProfiler,
  }: {
    [BabylonjsModule.INJECTABLE_TOKEN]: BabylonjsModule;
    [RenderManager.INJECTABLE_TOKEN]: RenderManager;
    [PerformanceProfiler.INJECTABLE_TOKEN]: PerformanceProfiler;
  }) {
    this.scene = babylonjs.scene;
    this.options = babylonjs.options;

    this.renderManager = renderManager;
    this.performanceProfiler = performanceProfiler;

    this.setResolution(this.options.qualitySettings.resolution);
    onAllModelLoaded.addOnce(() => {
      const options = this.getPostprocessOptions();
      if (options) onUpdatePostprocessOptionsFromJson.notifyObservers(options);
    });
  }

  public dispose() {
    this.postProcess?.dispose();
  }

  public async loadPostProcess(src: string) {
    Profile.start('Load PostProcess');

    const postProcessOptions = await loadJson<PostprocessOptions>(src);

    Profile.end('Load PostProcess');
    this.performanceProfiler.addCustom('Common', 'Load PostProcess', Profile.get('Load PostProcess'));

    return postProcessOptions;
  }
  public async applyPostProcess(src: string) {
    const settings = await this.loadPostProcess(src);
    if (settings) {
      this.updatePostprocessOptions(settings, true);
    }
  }

  protected initPostprocessOptions?: PostprocessOptions;
  public updatePostprocessOptions(options: Partial<PostprocessParameters>, fromJson: boolean = false) {
    const scene = EngineStore.LastCreatedScene;
    if (!scene) return;
    if (!this.postProcess) {
      this.postProcess = new ZEDRenderingPipeline('default', options, scene, this.renderManager, scene.cameras);
    } else {
      this.postProcess.updateOptions(options);
    }

    if (fromJson) {
      const currentOptions = this.getPostprocessOptions();
      if (currentOptions) {
        this.initPostprocessOptions = currentOptions;
        onUpdatePostprocessOptionsFromJson.notifyObservers(currentOptions);
      }
    }
  }
  public getPostprocessOptions() {
    return this.postProcess?.getOptions();
  }
  public getInitPostprocessOptions() {
    return this.initPostprocessOptions;
  }

  //-------------------------------------------------
  public getResolution() {
    return this.renderManager.getResolution();
  }
  public setResolution(resolution: Resolution) {
    this.renderManager.setResolution(resolution);
  }
}
